import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { CheckCircle, Database, GitBranch, Settings, Lock } from 'lucide-react';
import { ContactForm } from '../components/ContactForm';
import { SEO } from '../components/SEO';

export const Infrastructure = () => {
  const features = [
    {
      icon: <Database className="w-10 h-10 text-[#00890b]" />,
      title: 'Types & Attributes',
      description: 'Custom business objects, attributes, and constraints',
    },
    {
      icon: <GitBranch className="w-10 h-10 text-[#00890b]" />,
      title: 'Life Cycles',
      description: 'State-based workflows and promotion paths',
    },
    {
      icon: <Settings className="w-10 h-10 text-[#00890b]" />,
      title: 'OIRs & Policies',
      description: 'Object Initialization Rules and policy management',
    },
    {
      icon: <Lock className="w-10 h-10 text-[#00890b]" />,
      title: 'Access Control',
      description: 'Role-based security and permission management',
    },
  ];

  const capabilities = [
    'Modeled Types & Attributes',
    'Soft Types Definition',
    'Business Rules Configuration',
    'Object Initialization Rules (OIRs)',
    'Number Generation Policies',
    'Life Cycle Templates',
    'State Transitions',
    'Workflow Templates',
    'Process Templates',
    'Queue Management',
    'Background Tasks',
    'Access Control Policies',
    'Team Templates',
    'Site & Organization Setup',
    'Container Management',
    'Library Configuration',
  ];

  return (
    <div className="min-h-screen">
      <SEO 
        title="Hire Windchill Infrastructure Expert | Outsource OIRs & Workflows Configuration"
        description="Hire expert Windchill Infrastructure developers. Outsource your OIRs, Life Cycles, and Workflows configuration to experienced professionals."
        keywords="hire windchill infrastructure expert, outsource windchill configuration, hire oirs expert, windchill workflows consultant, hire infrastructure developer"
        canonical="/hire-infrastructure-customization-expert"
      />
      <section className="bg-gradient-to-br from-white via-green-50 to-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <Badge className="mb-6 bg-[#00890b] hover:bg-[#006a08] text-white px-4 py-2">
              Infrastructure Experts
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Hire Windchill Infrastructure Customization Expert
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              Outsource your infrastructure development to experienced consultants. Hire expert
              Windchill Infrastructure specialists for OIRs, Life Cycles, and Workflows
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Windchill Infrastructure Expertise
          </h2>
          <p className="text-lg text-gray-600 mb-4">
            Windchill infrastructure forms the backbone of your PLM environment. Our infrastructure
            experts specialize in configuring and customizing the foundational elements that power
            your Windchill applications, including types, attributes, life cycles, workflows, OIRs,
            and access control.
          </p>
          <p className="text-lg text-gray-600">
            With 15+ years of experience, we design and implement robust infrastructure solutions
            that support your business processes while maintaining flexibility for future growth.
          </p>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Core Infrastructure Areas</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <Card key={index} className="text-center hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex justify-center mb-4">{feature.icon}</div>
                  <CardTitle className="text-lg">{feature.title}</CardTitle>
                  <CardDescription>{feature.description}</CardDescription>
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Infrastructure Capabilities
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {capabilities.map((capability, index) => (
              <div
                key={index}
                className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg hover:bg-green-50 transition-colors"
              >
                <CheckCircle className="w-6 h-6 text-[#00890b] flex-shrink-0" />
                <span className="text-gray-700 font-medium">{capability}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Professional Infrastructure Configuration
          </h2>
          <p className="text-lg text-gray-600 mb-4">
            Our Windchill infrastructure consultants understand the critical importance of proper
            foundation design. We follow PTC best practices and industry standards to deliver
            infrastructure solutions that are secure, scalable, and maintainable.
          </p>
          <p className="text-lg text-gray-600 mb-4">
            Whether you're implementing a new Windchill environment or optimizing an existing one,
            we provide comprehensive infrastructure services including:
          </p>
          <ul className="list-disc list-inside text-lg text-gray-600 space-y-2 ml-4">
            <li>Type and attribute modeling aligned with business requirements</li>
            <li>Life cycle design for effective state management</li>
            <li>Workflow configuration for automated business processes</li>
            <li>OIR implementation for consistent object initialization</li>
            <li>Access control policies for security and compliance</li>
            <li>Queue and background task optimization</li>
          </ul>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Why Infrastructure Matters
          </h2>
          <p className="text-lg text-gray-600 mb-4">
            A well-designed Windchill infrastructure ensures data consistency, process efficiency,
            and system performance. Poor infrastructure decisions can lead to maintenance
            challenges, upgrade complications, and business process limitations.
          </p>
          <p className="text-lg text-gray-600">
            Our infrastructure experts work closely with your business and IT teams to design
            solutions that balance flexibility with governance, enabling your organization to adapt
            to changing requirements while maintaining system integrity.
          </p>
        </div>
      </section>

      <ContactForm />
    </div>
  );
};